import { ActivityTrackerEventType } from '../activityTracker/constants';
import { useActivityTracker } from '../activityTracker/useActivityTracker';
import { useCurrentUser } from '../user/useCurrentUser';

import { type RecoveryOptions } from '@/modules/login/types';

export function useMFATracking() {
  const [_, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  function sendMFAView() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_OTP_VIEW);
  }

  function sendMFAOTPBackAction() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_OTP_BACK_ACTION);
  }

  function sendMFAHelpBackAction() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_HELP_BACK_ACTION);
  }

  function sendMFAHelpAction() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_OTP_HELP_ACTION);
  }

  function sendMFALoginAction() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_LOGIN_ACTION);
  }

  function sendMFAHelpView(isResendEnabled = true) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_HELP_VIEW, {
      isResendEnabled,
    });
  }

  function sendMFAHelpContinueAction(fallbackChoice: RecoveryOptions) {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_HELP_CONTINUE_ACTION,
      {
        fallbackChoice,
      }
    );
  }

  function sendMfaSettingsMainView(currentStatus: 'on' | 'off') {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_SETTINGS_MAIN_VIEW, {
      currentStatus,
    });
  }

  function sendMfaSettingsToggleAction(currentStatus: 'on' | 'off') {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETTINGS_TOGGLE_ACTION,
      {
        currentStatus,
      }
    );
  }

  function sendMfaSetupPhoneInputView() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_PHONE_INPUT_VIEW
    );
  }

  function sendMfaSetupPhoneInputCountryCodeAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_PHONE_INPUT_COUNTRY_CODE_ACTION
    );
  }

  function sendMfaSetupPhoneInputContinueAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_PHONE_INPUT_CONTINUE_ACTION
    );
  }

  function sendMfaSetupCompletionView() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_COMPLETION_VIEW
    );
  }

  function sendMfaAdoptionSplashView(flow: 'sellerhub' | 'profile') {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_ADOPTION_SPLASH_VIEW,
      {
        flow,
      }
    );
  }

  function sendMfaAdoptionSplashContinueAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_ADOPTION_SPLASH_CONTINUE_ACTION
    );
  }

  function sendMfaAdoptionSplashCancelAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_ADOPTION_SPLASH_CANCEL_ACTION
    );
  }

  function sendMfaSetupOtpInputContinueAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_OTP_INPUT_CONTINUE_ACTION
    );
  }

  function sendMfaSetupOtpInputView() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MFA_SETUP_OTP_INPUT_VIEW);
  }

  function sendMfaSetupCompletionCompletedAction() {
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MFA_SETUP_COMPLETION_COMPLETED_ACTION
    );
  }

  return {
    sendMFAView,
    sendMFAOTPBackAction,
    sendMFAHelpBackAction,
    sendMFAHelpAction,
    sendMFALoginAction,
    sendMFAHelpView,
    sendMFAHelpContinueAction,
    sendMfaSettingsMainView,
    sendMfaSettingsToggleAction,
    sendMfaSetupPhoneInputView,
    sendMfaSetupPhoneInputCountryCodeAction,
    sendMfaSetupPhoneInputContinueAction,
    sendMfaSetupCompletionView,
    sendMfaAdoptionSplashView,
    sendMfaAdoptionSplashContinueAction,
    sendMfaAdoptionSplashCancelAction,
    sendMfaSetupOtpInputContinueAction,
    sendMfaSetupOtpInputView,
    sendMfaSetupCompletionCompletedAction,
  };
}
